import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import Typography from "@material-ui/core/Typography";
import AsyncSelect from "react-select/async";
import DropZone from "../../../../share-components/upload/dropzone";
import {
    closeImportDialog,
    downloadSampleCsv,
    importSizeCategoryMeasurements,
} from "../../store/sizeCategoryMeasurementSlice";
import { brandAutoSearchApi, sellerAutoSearchApi } from "../../../../services/manage-product/setting/sizeCategoryMeasurementService";

export default function SizeCategoryMeasurementImportModal() {
    const dispatch = useDispatch();
    const importDialog = useSelector(({ product }) => product.sizeCategoryMeasurement.importDialog);
    const currentUser  = useSelector(({ authReducer }) => authReducer.data.user);
    const isSuperAdmin = currentUser?.role?.id === 1;

    const [uploadFile, setUploadFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [buttonDisable, setButtonDisable] = useState(false);
    const [sampleDownloading, setSampleDownloading] = useState(false);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [selectedBrand,  setSelectedBrand]  = useState(null);
    const [sellerError, setSellerError] = useState(false);

    const sellerPromise = (v) =>
        sellerAutoSearchApi({ value: v }).then(({ response }) =>
            Array.isArray(response) ? response.map(s => ({ value: s.id, label: s.shop_name || s.name })) : []
        );

    const brandPromise = (v) =>
        brandAutoSearchApi({ value: v, seller_id: selectedSeller?.value ?? null }).then(({ response }) =>
            Array.isArray(response) ? response.map(b => ({ value: b.id, label: b.name })) : []
        );

    const handleClose = () => {
        setUploadFile(null);
        setImportResult(null);
        setButtonDisable(false);
        setSelectedSeller(null);
        setSelectedBrand(null);
        setSellerError(false);
        dispatch(closeImportDialog());
    };

    const handleFileChange = (acceptedFiles) => {
        setUploadFile(acceptedFiles);
        setImportResult(null);
    };

    const handleImport = () => {
        if (!uploadFile || !uploadFile[0]) return;
        if (isSuperAdmin && !selectedSeller) {
            setSellerError(true);
            return;
        }
        setSellerError(false);
        setButtonDisable(true);
        dispatch(importSizeCategoryMeasurements({
            file:     uploadFile[0],
            sellerId: selectedSeller?.value ?? null,
            brandId:  selectedBrand?.value  ?? null,
        })).then((res) => {
            setButtonDisable(false);
            if (res.payload) {
                setImportResult(res.payload);
            }
        });
    };

    const handleSampleDownload = (e) => {
        e.preventDefault();
        if (sampleDownloading) return;
        setSampleDownloading(true);
        dispatch(downloadSampleCsv()).then((res) => {
            setSampleDownloading(false);
            if (res.payload) {
                const url = window.URL.createObjectURL(new Blob([res.payload]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'size-category-measurement-template.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        });
    };

    return (
        <Modal size="lg" {...importDialog.props} toggle={handleClose}>
            <ModalHeader toggle={handleClose}>Import Size Category Measurements</ModalHeader>
            <ModalBody>
                <Row className="mb-3">
                    {isSuperAdmin && (
                        <Col md={6}>
                            <label className="col-form-label pt-0 pb-1 small">
                                Seller <span style={{ color: 'red' }}>*</span>
                            </label>
                            <AsyncSelect
                                isClearable
                                cacheOptions
                                defaultOptions
                                loadOptions={sellerPromise}
                                value={selectedSeller}
                                onChange={(opt) => {
                                    setSelectedSeller(opt ?? null);
                                    setSelectedBrand(null);
                                    if (opt) setSellerError(false);
                                }}
                                placeholder="Search seller..."
                            />
                            {sellerError && (
                                <div className="text-danger small mt-1">Seller is required.</div>
                            )}
                        </Col>
                    )}
                    <Col md={isSuperAdmin ? 6 : 12}>
                        <label className="col-form-label pt-0 pb-1 small">
                            Brand <small className="text-muted">(optional)</small>
                        </label>
                        <AsyncSelect
                            key={selectedSeller?.value ?? 'no-seller'}
                            isClearable
                            cacheOptions
                            defaultOptions={isSuperAdmin ? !!selectedSeller : true}
                            loadOptions={brandPromise}
                            value={selectedBrand}
                            onChange={(opt) => setSelectedBrand(opt ?? null)}
                            placeholder="Search brand..."
                            isDisabled={isSuperAdmin && !selectedSeller}
                        />
                    </Col>
                </Row>

                <Row className="mt-3 mb-3">
                    <Col md={12} className="mt-2">
                        <div className="product-box">
                            <div className="product-img pt-3 pb-3">
                                <DropZone
                                    extension=".csv, .xlsx, .xls"
                                    onChangeFile={handleFileChange}
                                />
                            </div>
                            {uploadFile && (
                                <Typography>File: {uploadFile[0].name}</Typography>
                            )}
                        </div>
                    </Col>
                    <Col md={9} />
                    <Col md={3} className="mt-4">
                        <button
                            className="btn btn-success btn-block"
                            disabled={buttonDisable || !uploadFile}
                            onClick={handleImport}
                        >
                            {buttonDisable ? (
                                <i className="fa fa-spinner fa-spin fa-lg spinner" />
                            ) : (
                                'Import'
                            )}
                        </button>
                    </Col>
                </Row>

                {importResult && importResult.successCount > 0 && (
                    <Row>
                        <Col md={12}>
                            <h6 className="text-success">
                                {importResult.successCount} record(s) imported successfully
                                {importResult.totalCount > importResult.successCount && (
                                    <span> ({importResult.totalCount - importResult.successCount} failed)</span>
                                )}
                            </h6>
                        </Col>
                    </Row>
                )}

                {importResult && importResult.errors && importResult.errors.length > 0 && (
                    <Row>
                        <Col md={12}>
                            <h6 className="text-danger">{importResult.errors.length} row(s) could not be imported:</h6>
                            <ul>
                                {importResult.errors.map((error, index) => (
                                    <li key={index} className="text-danger">{error}</li>
                                ))}
                            </ul>
                            {importResult.errorFileUrl && (
                                <a href={importResult.errorFileUrl} download className="text-danger font-weight-bold">
                                    <i className="fa fa-download mr-1" aria-hidden="true" />
                                    Download Error Report
                                </a>
                            )}
                        </Col>
                    </Row>
                )}

                <ModalFooter className="mt-2">
                    <a href="#" style={{ color: "black", fontWeight: "normal", pointerEvents: sampleDownloading ? 'none' : 'auto' }} onClick={handleSampleDownload}>
                        {sampleDownloading ? (
                            <i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />
                        ) : (
                            <i className="fa fa-download mr-1" aria-hidden="true" />
                        )}
                        Download Sample CSV
                    </a>
                </ModalFooter>
            </ModalBody>
        </Modal>
    );
}
